/** Те, що обхідникові треба від CRUD-контексту ядра. */
interface EditCrudContext {
    contextType?: string
    currentDetailMode?: string
    resource?: { detailConfig?: { modeConfig?: { edit?: { displayMode?: string } } } }
    actions?: Record<string, unknown> & {
        // eslint-disable-next-line no-unused-vars -- імена параметрів у типі функції
        edit?: (item: unknown, params?: Record<string, unknown>) => unknown
        reloadData?: () => unknown
    }
    // eslint-disable-next-line no-unused-vars -- ім'я параметра в типі функції
    eventManager?: { emit: (...args: unknown[]) => unknown }
    [key: string]: unknown
}

/** Позначка, що `edit` уже обгорнуто: контекст із реєстру не обгортається вдруге. */
const WRAPPED = Symbol('editInDrawerFromPage')

/**
 * «Редагувати» в картці, відкритій прямою адресою, — у шухляді
 * (Ship-обхід вади ядра, HANDOFF «Технічний борг», `SPEC-017` S9a).
 *
 * **Вада.** Картка за прямою адресою (`/clients/{uuid}`, `/users/{uuid}`)
 * відкривається в режимі `page`, і ядровий `EditActionHandlerTask` веде
 * «Редагувати» на `/…/{uuid}/edit`. Такої адреси ядро не обробляє
 * (`ParseResourceUrlTask` знає лише список і перегляд), і `UniversalResourcePage`
 * малює порожній реєстр замість форми. Зі списку все гаразд: там картка —
 * шухляда.
 *
 * **Обхід.** Для картки в режимі `page` «Редагувати» відкриває форму
 * шухлядою (`displayMode: 'drawer'`) поверх сторінки, а закриття шухляди
 * перечитує сторінку (`reloadData` — ядро передбачило його саме для цього).
 * Ресурс, який сам задав `detailConfig.modeConfig.edit.displayMode`, і явні
 * параметри виклику мають перевагу — обхідник їх не чіпає.
 *
 * Глобально — обробником `onAfterInit` у `CrudResourceConfig`, а не рядком у
 * кожному ресурсі: вада однакова для всіх карток, і новий ресурс не мусить
 * про неї пам'ятати. Виправлення в ядрі (сабмодуль) — окремим погодженням.
 */
export const EditInDrawerFromPageTask = ({ crudContext }: { crudContext?: EditCrudContext }): void => {
    const actions = crudContext?.actions
    const edit = actions?.edit

    if (!crudContext || !actions || typeof edit !== 'function' || (edit as unknown as Record<symbol, boolean>)[WRAPPED]) {
        return
    }

    const wrapped = (item: unknown, params?: Record<string, unknown>) => {
        const configured = crudContext.resource?.detailConfig?.modeConfig?.edit?.displayMode
        const isPageCard = crudContext.contextType === 'detail' && crudContext.currentDetailMode === 'page'

        if (params?.['displayMode'] || configured || !isPageCard) return edit(item, params)

        return edit(item, {
            ...params,
            displayMode: 'drawer',
            // Сторінку шухляда не заміщує, а лягає поверх неї
            replace: false,
            onClose: () => {
                // Сторінка під шухлядою показує те, що щойно зберегли
                void Promise.resolve(crudContext.actions?.reloadData?.()).catch((error: unknown) => {
                    console.warn('EditInDrawerFromPageTask: не вдалося перечитати картку', error)
                })

                crudContext.eventManager?.emit('onCloseDrawer', { crudContext })
            },
        })
    }

    ;(wrapped as unknown as Record<symbol, boolean>)[WRAPPED] = true

    crudContext.actions = { ...actions, edit: wrapped }
}
